import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { MarketplaceQuantityControl } from "./MarketplaceQuantityControl";
import { formatWooPrice } from "../utils/addressHelpers";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import { marketplaceProductCardSx } from "../constants/marketplaceCardStyles";
import type { WooProduct } from "../types";
import { ProductGridSkeleton } from "./MarketplaceSkeletons";
import {
  canIncreaseQuantity,
  canIncreaseQuantityWithRelease,
  getEffectivePurchaseLimit,
  getProductStockLimit,
  isLowStock,
} from "../utils/stockHelpers";

interface WooProductGridProps {
  products: WooProduct[];
  loading: boolean;
  error: string | null;
  onAddToCart: (product: WooProduct) => void;
  getItemQuantity?: (productId: number) => number;
  onUpdateQuantity?: (productId: number, quantity: number) => void;
  /** Oculta preço nos cards (fluxo profissional de liberação). Default false. */
  hidePrice?: boolean;
  /** Cards menores e grid mais denso (tela de liberação). Default false. */
  compact?: boolean;
  /** Catálogo visível, mas compra bloqueada até liberação da loja (modo legado). */
  purchaseLocked?: boolean;
  /** Limite liberado via MedicationDispense — undefined desliga o modo por produto. */
  getReleaseLimit?: (productId: number) => number;
  /** Código ISO da moeda da loja. Default BRL. */
  currencyCode?: string;
}

export function WooProductGrid({
  products,
  loading,
  error,
  onAddToCart,
  getItemQuantity,
  onUpdateQuantity,
  hidePrice = false,
  compact = false,
  purchaseLocked = false,
  getReleaseLimit,
  currencyCode = "BRL",
}: WooProductGridProps) {
  const theme = useTheme();

  if (loading) {
    return <ProductGridSkeleton compact={compact} />;
  }

  if (error) {
    return (
      <Typography sx={{ ...mt.body, color: "error.main" }} textAlign="center" py={2}>
        {error}
      </Typography>
    );
  }

  if (products.length === 0) {
    return (
      <Typography sx={{ ...mt.body, color: "text.secondary" }} textAlign="center" py={4}>
        Nenhum produto encontrado.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact
          ? {
              xs: "repeat(auto-fill, minmax(116px, 182px))",
              sm: "repeat(auto-fill, minmax(120px, 188px))",
              md: "repeat(auto-fill, minmax(124px, 196px))",
              lg: "repeat(auto-fill, minmax(128px, 204px))",
            }
          : {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
            },
        gap: compact ? 0.75 : 1.25,
        ...(compact ? { justifyContent: "start" } : {}),
      }}
    >
      {products.map((product) => {
        const imageSrc =
          product.images[0]?.thumbnail ?? product.images[0]?.src ?? "";
        const displayPrice =
          product.onSale && product.salePrice
            ? product.salePrice
            : product.price || product.regularPrice;
        const outOfStock =
          product.stockStatus === "outofstock" || !product.purchasable;
        const qty = getItemQuantity?.(product.id) ?? 0;
        const releaseMode = Boolean(getReleaseLimit);
        const releasedQty = releaseMode ? getReleaseLimit!(product.id) : 0;
        const hasRelease = releaseMode && releasedQty > 0;
        const locked = releaseMode
          ? !hasRelease && !outOfStock
          : purchaseLocked && !outOfStock;
        const inCart = qty > 0 && !locked;
        const stockLimit = getProductStockLimit(product);
        const effectiveLimit = releaseMode
          ? getEffectivePurchaseLimit(product, releasedQty)
          : stockLimit;
        const lowStock = isLowStock(product);

        return (
          <Stack
            key={product.id}
            sx={{
              width: "100%",
              overflow: "hidden",
              ...marketplaceProductCardSx(theme),
              ...(compact
                ? {
                    "&:hover": {
                      transform: "none",
                    },
                  }
                : {}),
              ...(locked
                ? {
                    opacity: 0.62,
                    filter: "grayscale(0.35)",
                  }
                : {}),
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  bgcolor: alpha(theme.palette.primary800.main, 0.04),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: compact ? 0.5 : 1,
                  overflow: "hidden",
                }}
              >
                {imageSrc ? (
                  <Box
                    component="img"
                    src={imageSrc}
                    alt={product.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                    Sem imagem
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: compact ? 4 : 8, left: compact ? 4 : 8 }}>
                {product.onSale ? (
                  <Chip label="Promoção" size="small" color="secondary" sx={{ height: compact ? 20 : 22, fontSize: compact ? "0.68rem" : undefined }} />
                ) : null}
                {lowStock ? (
                  <Chip label="Últimas unidades" size="small" color="warning" sx={{ height: compact ? 20 : 22, fontSize: compact ? "0.68rem" : undefined }} />
                ) : null}
              </Stack>
            </Box>

            <Stack spacing={compact ? 0.75 : 0.75} sx={{ p: compact ? 1.15 : 1.25, flex: 1, minHeight: compact ? 80 : 88 }}>
              <Typography
                sx={{
                  ...mt.bodySecondary,
                  fontWeight: 600,
                  minHeight: compact ? 38 : 40,
                  ...(compact
                    ? { fontSize: "0.8125rem", lineHeight: 1.32 }
                    : {}),
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {product.name}
              </Typography>

              {!hidePrice && (
                <Stack direction="row" spacing={0.5} alignItems="baseline" flexWrap="wrap">
                  <Typography sx={{ ...mt.price, color: "primary800.main" }}>
                    {formatWooPrice(displayPrice, currencyCode)}
                  </Typography>
                  {product.onSale && product.regularPrice ? (
                    <Typography
                      sx={{
                        ...mt.bodySecondary,
                        color: "text.secondary",
                        textDecoration: "line-through",
                      }}
                    >
                      {formatWooPrice(product.regularPrice, currencyCode)}
                    </Typography>
                  ) : null}
                </Stack>
              )}

              {hasRelease ? (
                <Chip
                  label={`Liberado: ${releasedQty} un.`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    alignSelf: "flex-start",
                    height: compact ? 22 : 24,
                    fontWeight: 700,
                    fontSize: compact ? "0.6875rem" : "0.75rem",
                  }}
                />
              ) : null}

              {inCart && onUpdateQuantity ? (
                <Box
                  sx={{
                    mt: "auto",
                    p: compact ? 0.5 : 0.75,
                    borderRadius: compact ? 1 : 1.5,
                    bgcolor: "action.hover",
                  }}
                >
                  <MarketplaceQuantityControl
                    quantity={qty}
                    stockLimit={effectiveLimit}
                    canIncrease={
                      releaseMode
                        ? canIncreaseQuantityWithRelease(product, qty, releasedQty)
                        : canIncreaseQuantity(product, qty)
                    }
                    onChange={(nextQty) => onUpdateQuantity(product.id, nextQty)}
                  />
                </Box>
              ) : (
                <Button
                  variant="contained"
                  size={compact ? "small" : "medium"}
                  disabled={outOfStock || locked}
                  onClick={() => onAddToCart(product)}
                  sx={{
                    mt: "auto",
                    minHeight: compact ? 36 : 40,
                    py: compact ? 0.4 : undefined,
                    fontSize: compact ? "0.8125rem" : undefined,
                    ...mt.button,
                    textTransform: "none",
                    bgcolor: locked ? "grey.400" : "primary800.main",
                    "&:hover": {
                      bgcolor: locked ? "grey.400" : "primary900.main",
                    },
                    ...(locked
                      ? {
                          whiteSpace: "normal",
                          lineHeight: 1.25,
                          px: 1,
                        }
                      : {}),
                  }}
                >
                  {outOfStock
                    ? "Indisponível"
                    : locked
                      ? "Esperando liberação da loja"
                      : "Adicionar"}
                </Button>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
}
